import { useMemo } from 'react'
import { useQueries } from 'react-query'

import { BankLine } from '@modules-deprecated/bankReconciliation/types'

import { QueryKeys } from '../../../enums/queryKeys'
import { getBankLineMatchSuggestions } from '../query-api'
import { BookkeepingItem } from '../types/bookkeepingItem'
import { getBankLineIdPages, getSuggestionsByBankLineId, getUnmatchedBankLineIds } from '../utils'

interface UseBankLineSuggestionsMapProps {
  accountId?: string
  bankLines: BankLine[]
  bookkeepingItems?: BookkeepingItem[]
  enabled?: boolean
  organizationId?: string
}

export const useBankLineSuggestionsMap = ({
  accountId,
  bankLines,
  bookkeepingItems,
  enabled: enabledProp = true,
  organizationId,
}: UseBankLineSuggestionsMapProps) => {
  const bankLineIdPages = useMemo(() => getBankLineIdPages(bankLines), [bankLines])
  const enabled = enabledProp && !!organizationId && !!accountId

  const suggestionQueries = useQueries(
    bankLineIdPages.map((bankLineIds) => {
      const unmatchedBankLineIds = getUnmatchedBankLineIds(bankLines, bankLineIds)

      return {
        queryKey: [QueryKeys.BankLineSuggestions, accountId, bankLineIds],
        queryFn: () =>
          getBankLineMatchSuggestions({
            organizationId: organizationId as string,
            accountId: accountId as string,
            bankLineIds: unmatchedBankLineIds,
          }),
        enabled: enabled && unmatchedBankLineIds.length > 0,
        keepPreviousData: true,
      }
    }),
  )

  const suggestionsByBankLineId = useMemo(() => {
    const suggestions = suggestionQueries.flatMap((query) => Object.values(query.data?.suggestions ?? []))

    return getSuggestionsByBankLineId(suggestions, bookkeepingItems)
  }, [bookkeepingItems, suggestionQueries])

  return {
    suggestionsByBankLineId,
    isLoading: suggestionQueries.some((query) => query.isLoading),
  }
}
